import React from "react";
import ReactDOM from "react-dom";
import { PublicLayout, usePublicData, mediaUrl, isVideoUrl, availClass } from "@/pages/public/PublicLayout";
import { InquireModal, useInquire } from "@/pages/public/InquireModal";
import { useDrop, money } from "@/pages/public/DropBuilderCtx";
import { useBodyLock } from "@/pages/public/useBodyLock";

const ASSET = "/2high-assets";
const fallbackPool = [
  "/assets/img/rainbow-lights-md.webp",
  "/assets/img/galactic-gas-md.webp",
  "/assets/img/moon-fuel-md.webp",
  "/assets/img/astro-runtz-md.webp",
  "/assets/img/moon-mix-md.webp",
  "/assets/img/explorer-pack-md.webp",
  "/assets/img/curated-oz-md.webp",
  "/assets/img/matte-pouch-md.webp",
];
const fallbackImg = (i) => `${ASSET}${fallbackPool[i % fallbackPool.length]}`;

// Visual copy per category id — eyebrow/headline/lead. The label comes live from the
// Categories admin so renames propagate. If a category id isn't mapped here we fall back
// Public menu section copy is fully operator-editable in the Owner Cabinet
// (Categories tab → expand any row → edit "Headline", "Headline accent",
// "Lead paragraph"). The values come straight from the Category model and
// fall back to the category label / id if a row was somehow left blank.
//
// The fallback constants below are ONLY used the very first time before
// the startup seed has had a chance to backfill — kept as a safety net.
const FALLBACK_COPY = {
  flower:       { h: "Indoor",       em: "Flower",       lead: "Hand-selected indoor strains. Eighth, quarter, half, or full ounce — pick & mix any combo." },
  breakdown:    { h: "Flower",       em: "Breakdowns",   lead: "Eighths and quarters of our top-shelf indoor — perfect for sampling the full lineup." },
  wholesale:    { h: "Wholesale",    em: "Assortments",  lead: "Quarter pound and up. Private pricing. Lead times listed below." },
  edibles:      { h: "Edibles &",    em: "Gummies",      lead: "Indoor-derived. Hand-portioned. Lab-tested dosing." },
  dispos:       { h: "Carts &",      em: "Disposables",  lead: "Live resin and live rosin. Single-strain. Ceramic coil." },
  concentrates: { h: "Concentrates", em: "& Hash",       lead: "Solventless rosin, live resin, hash rosin and traditional hash." },
  infused:      { h: "Infused",      em: "Flower",       lead: "Top-shelf indoor flower coated in live rosin or kief." },
  moonrocks:    { h: "Snowballs",    em: "& Moonrocks",  lead: "Coated, kiefed, layered. The heaviest end of the shelf." },
};

export default function PublicMenu() {
  return (
    <PublicLayout>
      <MenuBody />
      <InquireModal />
    </PublicLayout>
  );
}

function MenuBody() {
  const { products, categories } = usePublicData();

  // Build the menu from the live Categories admin: only visible categories,
  // sorted by `sort`. Reorder + hide just work — no code changes needed.
  const sections = React.useMemo(() => {
    const list = (categories || []).filter(c => c.visible !== false);
    list.sort((a, b) => (a.sort || 0) - (b.sort || 0));
    return list.map((c, i) => {
      // Prefer admin-edited copy; fall back to the static constants for
      // any newly-added category where the operator hasn't set custom text.
      const fb = FALLBACK_COPY[c.id] || {};
      const num = String(i + 1).padStart(2, "0");
      const eyebrowSlug = (c.label || c.id || "").toUpperCase();
      return {
        id: c.id,
        cat: c.id,
        eye: `— SECTION ${num} · ${eyebrowSlug}`,
        h:    c.headline    || fb.h    || c.label || c.id,
        em:   c.headline_em || fb.em   || "",
        lead: c.lead        || fb.lead || "",
      };
    });
  }, [categories]);

  // Scroll to hash (e.g. /menu#breakdown) after mount
  React.useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [sections]);

  const byCat = React.useMemo(() => {
    const map = {};
    (products || []).filter(p => p.show_in_full_menu !== false).forEach(p => {
      const k = (p.category || "flower").toLowerCase();
      (map[k] = map[k] || []).push(p);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.sort || 0) - (b.sort || 0)));
    return map;
  }, [products]);

  return (
    <div style={{ paddingTop: 80 }}>
      {sections.map((sec) => {
        const rows = byCat[sec.cat] || [];
        return (
          <section className="fm-section" id={sec.id} key={sec.id} data-testid={`fm-${sec.id}`}>
            <div className="eye">{sec.eye}</div>
            <h2>{sec.h} {sec.em && <em>{sec.em}</em>}</h2>
            {sec.lead && <p className="lead">{sec.lead}</p>}
            {/* Mix & Match cinematic flow — only above flower breakdowns.
                Wholesale doesn't need it (members understand pound-and-up). */}
            {sec.cat === "breakdown" && <MixBanner />}
            {sec.cat === "flower" && <MixBanner variant="flower" />}
            <ProductRail items={rows} sectionId={sec.id} />
          </section>
        );
      })}
    </div>
  );
}

/* ====================================================================
   Mix & Match cinematic banner. Editorial copy, NOT salesy.
   ==================================================================== */
/* ====================================================================
   Mix & Match cinematic banner — slim, editorial, 5-step rotation flow
   that members can read in 3 seconds. Each step is clickable: it
   filters the menu to the relevant section and scrolls there.
   ==================================================================== */
const ROTATION_STEPS = [
  { id: "flight",  name: "Flavor Flight",     spec: "2 × 3.5",   anchor: "#flower",    hl: "3.5" },
  { id: "zip",     name: "Custom Zip",        spec: "8 × 3.5",   anchor: "#flower",    hl: "3.5" },
  { id: "qp",      name: "Quarter Rotation",  spec: "4 × ZIP",   anchor: "#flower",    hl: "ZIP" },
  { id: "hp",      name: "Half Rotation",     spec: "8 × ZIP",   anchor: "#flower",    hl: "ZIP" },
  { id: "fulldrop",name: "Full Drop",         spec: "16 × ZIP",  anchor: "#wholesale", hl: "P"   },
];

function MixBanner({ variant = "breakdown" }) {
  const eye = variant === "wholesale" ? "DROP BUILDER · WHOLESALE FLOW"
            : variant === "flower"    ? "DROP BUILDER · MIX FREELY"
            :                           "DROP BUILDER · MIX FREELY";
  const h   = variant === "wholesale" ? "Stack your" : "Build your";
  const em  = variant === "wholesale" ? "shelf."     : "rotation.";
  const sub = variant === "wholesale"
    ? "From a single P to a full bird — mix any strain, any quantity."
    : "From single jars to full 16-flavor drops — mix any strain, any quantity.";

  const handleStep = (step) => {
    // Smooth-scroll to anchor and pulse the matching tier pill class
    const el = document.querySelector(step.anchor);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Briefly broadcast a highlight pulse — TierPicker reads this via state
    try {
      window.dispatchEvent(new CustomEvent("2high:tier-pulse", { detail: { key: step.hl, scope: step.anchor } }));
    } catch (_) {}
  };

  return (
    <div className={`mix-flow ${variant === "wholesale" ? "is-wholesale" : ""}`} data-testid={`mix-banner-${variant}`}>
      <div className="mix-flow__head">
        <div className="mix-flow__eye">— {eye}</div>
        <h3 className="mix-flow__h"><span>{h}</span> <em>{em}</em></h3>
        <p className="mix-flow__sub">{sub}</p>
      </div>
      <div className="mix-flow__rail" role="list">
        {ROTATION_STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            <button
              type="button"
              className="mix-step"
              data-testid={`mix-step-${s.id}`}
              onClick={() => handleStep(s)}
              role="listitem"
              aria-label={`${s.name} · ${s.spec}`}
            >
              <span className="mix-step__num">{String(i + 1).padStart(2, "0")}</span>
              <span className="mix-step__name">{s.name}</span>
              <span className="mix-step__spec">{s.spec}</span>
            </button>
            {i < ROTATION_STEPS.length - 1 && (
              <span className="mix-step__sep" aria-hidden="true">→</span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function ProductRail({ items, sectionId }) {
  const railRef = React.useRef(null);
  const [canPrev, setCanPrev] = React.useState(false);
  const [canNext, setCanNext] = React.useState(true);

  const update = React.useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  React.useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update, items.length]);

  const scrollBy = (dir) => {
    const el = railRef.current;
    if (!el) return;
    const delta = (el.clientWidth || 600) * 0.85 * dir;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  // Placeholders when CMS has no items in this category — render as clean
  // empty-state cards (gradient + "+" icon), NOT stock-photo dummies.
  const list = items.length > 0 ? items : Array.from({ length: 3 }).map((_, i) => ({
    id: `${sectionId}-ph-${i}`,
    name: `[Item 0${i + 1}]`,
    notes: "",
    size: "",
    availability: "Coming soon",
    badge: "COMING SOON",
    img: "",
    _placeholder: true,
  }));

  return (
    <div className="prail-wrap" data-testid={`rail-${sectionId}`}>
      <button
        type="button"
        className="prail-btn prev"
        onClick={() => scrollBy(-1)}
        disabled={!canPrev}
        aria-label="Scroll left"
        data-testid={`rail-${sectionId}-prev`}
      >‹</button>

      <div className="prail pgrid" ref={railRef} id={`g${cap(sectionId)}`}>
        {list.map((p, i) => <Pcard key={p.id || i} p={p} i={i} />)}
      </div>

      <button
        type="button"
        className="prail-btn next"
        onClick={() => scrollBy(1)}
        disabled={!canNext}
        aria-label="Scroll right"
        data-testid={`rail-${sectionId}-next`}
      >›</button>
    </div>
  );
}

function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ""; }

function Pcard({ p, i }) {
  const inquire = useInquire();
  const [showDetail, setShowDetail] = React.useState(false);

  // Build media list. For placeholder cards (`_placeholder`), skip stock-photo
  // fallbacks entirely — the Pcard renders a clean gradient empty-state instead.
  // Each item carries TWO URLs:
  //   url      → small WebP, used in the rail card thumbnail (fast)
  //   urlLarge → large WebP, used in the full-screen detail modal (sharp)
  const items = React.useMemo(() => {
    if (p._placeholder) return [];
    const out = [];
    const seen = new Set();
    const pushImg = (smallRaw, largeRaw, key) => {
      const url = smallRaw ? mediaUrl(smallRaw) : "";
      if (!url || seen.has(url)) return;
      seen.add(url);
      out.push({
        kind: "image",
        url,
        urlLarge: largeRaw ? mediaUrl(largeRaw) : url,
        key,
      });
    };

    // Cover
    const coverSmall = (p.img_set && (p.img_set.sm || p.img_set.md || p.img_set.lg)) || p.img || "";
    const coverLarge = (p.img_set && (p.img_set.lg || p.img_set.md || p.img_set.sm)) || p.img || "";
    if (coverSmall) {
      const u = mediaUrl(coverSmall);
      seen.add(u);
      out.push({ kind: "image", url: u, urlLarge: mediaUrl(coverLarge), key: "cover" });
    }

    // Legacy img2 (single URL only)
    if (p.img2) pushImg(p.img2, p.img2, "img2");

    // Extra photos[] — pull from the photo's own set so each photo gets the
    // correct small/large pair, not just the cover's set.
    if (Array.isArray(p.photos)) {
      p.photos.forEach((ph, idx) => {
        if (typeof ph === "string") {
          pushImg(ph, ph, `ph${idx}`);
        } else if (ph && ph.url) {
          const sm = ph.set?.sm || ph.url;
          const lg = ph.set?.lg || ph.set?.md || ph.url;
          pushImg(sm, lg, `ph${idx}`);
        }
      });
    }

    // Videos
    if (Array.isArray(p.videos)) {
      p.videos.forEach((v, idx) => {
        const url = mediaUrl(v.web_url || v.url || "");
        if (!url || seen.has(url)) return;
        seen.add(url);
        out.push({
          kind: "video",
          url,
          urlLarge: url,
          poster: mediaUrl(v.poster_url || "") || mediaUrl(coverLarge),
          key: `v${idx}`,
        });
      });
    }
    return out;
  }, [p, i]);

  const [idx, setIdx] = React.useState(0);
  const total = items.length;
  const cur = items[idx] || items[0] || null;
  const isEmpty = !cur;

  // Touch/swipe handling
  const touch = React.useRef({ x: 0, y: 0, t: 0 });
  function onTouchStart(e) {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  }
  function onTouchEnd(e) {
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0 && idx < total - 1) setIdx(idx + 1);
      if (dx > 0 && idx > 0) setIdx(idx - 1);
    }
  }

  const badge = (p.badge || (p.tags && p.tags[0]) || "").toString().toUpperCase() || "DROP";
  const av = p.availability || "In stock";
  const avCls = availClass(av);

  const bgUrl = cur ? (cur.kind === "video" ? cur.poster : cur.url) : "";

  return (
    <>
    <div className="pcard" data-testid={`pcard-${p.id || i}`}>
      <span className={`pbdg ${avCls}`}>{badge}</span>

      <div
        className="pimg pmedia"
        style={isEmpty ? {
          background: "radial-gradient(circle at 50% 35%, rgba(201,168,107,.14), rgba(20,18,11,.95) 70%)",
          border: "1px dashed rgba(201,168,107,.28)",
          display: "grid",
          placeItems: "center",
          cursor: "default",
        } : { backgroundImage: `url('${bgUrl}')`, cursor: "pointer" }}
        onTouchStart={isEmpty ? undefined : onTouchStart}
        onTouchEnd={isEmpty ? undefined : onTouchEnd}
        onClick={isEmpty ? undefined : (e) => {
          // Don't open the modal when the user is interacting with controls
          if (e.target.closest(".pmedia-nav, .pmedia-dot, video")) return;
          setShowDetail(true);
        }}
        data-testid={`pcard-media-${p.id || i}`}
      >
        {isEmpty && (
          <div style={{ textAlign: "center", color: "#c9a86b", opacity: .55, pointerEvents: "none" }}>
            <div style={{ fontSize: 32, fontWeight: 300, lineHeight: 1 }}>+</div>
            <div style={{ fontSize: 9.5, letterSpacing: ".22em", textTransform: "uppercase", marginTop: 8 }}>Coming Soon</div>
          </div>
        )}
        {!isEmpty && cur.kind === "video" && (
          <video
            key={cur.key}
            src={cur.url}
            poster={cur.poster}
            muted
            loop
            playsInline
            preload="none"
            controls
          />
        )}

        {total > 1 && (
          <>
            <button
              type="button"
              className="pmedia-nav prev"
              data-testid={`pmedia-prev-${p.id || i}`}
              aria-label="Previous media"
              disabled={idx === 0}
              onClick={(e) => { e.stopPropagation(); setIdx(Math.max(0, idx - 1)); }}
            >‹</button>
            <button
              type="button"
              className="pmedia-nav next"
              data-testid={`pmedia-next-${p.id || i}`}
              aria-label="Next media"
              disabled={idx === total - 1}
              onClick={(e) => { e.stopPropagation(); setIdx(Math.min(total - 1, idx + 1)); }}
            >›</button>
            <div className="pmedia-dots" data-testid={`pmedia-dots-${p.id || i}`}>
              {items.map((it, k) => (
                <button
                  key={it.key}
                  type="button"
                  className={`pmedia-dot${k === idx ? " on" : ""}`}
                  aria-label={`Media ${k + 1}`}
                  onClick={(e) => { e.stopPropagation(); setIdx(k); }}
                />
              ))}
            </div>
            <div className="pmedia-count">{idx + 1} / {total}</div>
          </>
        )}
      </div>

      <h3 onClick={() => setShowDetail(true)} style={{ cursor: "pointer" }} data-testid={`pcard-name-${p.id || i}`}>{p.name || "Product"}</h3>
      <div className="pmeta">
        {[p.section_label || defaultLabel(p.category), p.size].filter(Boolean).join(" · ")}
      </div>
      {p.price && (
        p.on_sale && p.compare_at_price ? (
          <div className="pnotes" style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "'JetBrains Mono', monospace", letterSpacing: ".18em", fontSize: 11 }}>
            <span style={{ padding: "2px 7px", borderRadius: 4, background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407", fontWeight: 700, letterSpacing: ".16em", fontSize: 9.5 }}>SALE</span>
            <s style={{ color: "#6e6f5f", textDecorationThickness: 1 }}>{p.compare_at_price}</s>
            <span style={{ color: "var(--accent)", fontWeight: 600 }}>{p.price}</span>
          </div>
        ) : (
          <div className="pnotes" style={{ color: "var(--accent)", fontStyle: "normal", fontFamily: "'JetBrains Mono', monospace", letterSpacing: ".2em", fontSize: 11 }}>{p.price}</div>
        )
      )}
      {p.notes && <div className="pnotes">{p.notes}</div>}
      <div className={`pavail ${avCls}`}>{av}</div>
      <div className="pacts">
        <button type="button" onClick={() => setShowDetail(true)} data-testid={`pcard-view-${p.id || i}`}>View Details</button>
        <button type="button" onClick={() => inquire.open({ productName: p.name })} data-testid={`inquire-${p.id || i}`}>Inquire</button>
        <button type="button" onClick={() => save(p)} data-testid={`save-${p.id || i}`}>Save</button>
      </div>
    </div>
    {showDetail && <ProductDetailModal p={p} items={items} onClose={() => setShowDetail(false)} />}
    </>
  );
}

function defaultLabel(cat) {
  const map = {
    flower: "Indoor Flower",
    breakdown: "Flower Breakdown",
    wholesale: "Wholesale",
    edibles: "Edibles",
    dispos: "Vape",
    concentrates: "Concentrate",
    infused: "Infused Flower",
    moonrocks: "Moonrocks",
  };
  return map[(cat || "").toLowerCase()] || "Product";
}

async function copy(p) {
  try {
    await navigator.clipboard.writeText([p.name, p.size, p.notes].filter(Boolean).join(" · "));
  } catch (_) {}
}
function save(p) {
  try {
    const key = "2h-saved";
    const cur = JSON.parse(localStorage.getItem(key) || "[]");
    if (!cur.includes(p.id)) { cur.push(p.id); localStorage.setItem(key, JSON.stringify(cur)); }
  } catch (_) {}
}

/* ============================================================
   ProductDetailModal — full-info popup when user clicks a card
   Shows all photos/videos in a big lightbox, full description,
   prices, batch info, tags, and a prominent Inquire CTA.
   ============================================================ */
function ProductDetailModal({ p, items, onClose }) {
  const inquire = useInquire();
  const [idx, setIdx] = React.useState(0);
  const total = items.length;
  const cur = items[idx] || items[0];

  // Lock body + html scroll via ref-counted hook — safe even when this
  // modal is stacked under the DropDrawer or image lightbox.
  useBodyLock(true);

  // Close on Escape
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setIdx((i) => Math.min(total - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total, onClose]);

  const av = p.availability || "In stock";
  const avCls = availClass(av);
  const tags = (p.tags || []).filter(Boolean);

  // Touch/swipe in modal
  const touch = React.useRef({ x: 0, y: 0 });
  const onTS = (e) => { const t = e.touches[0]; touch.current = { x: t.clientX, y: t.clientY }; };
  const onTE = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0 && idx < total - 1) setIdx(idx + 1);
      if (dx > 0 && idx > 0) setIdx(idx - 1);
    }
  };

  // Image lightbox state — tap any photo to view full-size
  const [zoomed, setZoomed] = React.useState(false);

  // Wheel handler — when the user scrolls anywhere over the backdrop
  // (including the empty space outside the card on desktop), forward the
  // scroll delta to the modal's scrollable `.pdm-info` content. Without
  // this the wheel event hits nothing scrollable in the locked viewport
  // and feels stuck.
  const onBackdropWheel = (e) => {
    const info = e.currentTarget.querySelector(".pdm-info");
    if (!info) return;
    // If the cursor is already over a scrollable target inside the modal,
    // let native scrolling do its thing.
    const path = typeof e.composedPath === "function" ? e.composedPath() : [];
    if (path.includes(info)) return;
    e.preventDefault();
    info.scrollTop += e.deltaY;
  };

  // Portal target — render at document.body so the modal escapes the
  // public layout's `.layer` stacking context (which has z-index: 2 and
  // would otherwise clamp the modal beneath the fixed top nav / sticky
  // bar / inquire FAB).
  return ReactDOM.createPortal((
    <div
      className="pdm-backdrop"
      data-testid={`pdm-${p.id}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onWheel={onBackdropWheel}
    >
      {/* X close button — rendered as backdrop child (NOT inside .pdm-card) so it
          stays anchored to viewport top-right and never moves with card scroll. */}
      <button className="pdm-x" onClick={onClose} data-testid="pdm-close" aria-label="Close">✕</button>

      <div className="pdm-card">
        {/* Left: media gallery — modal uses the LARGE size for sharp full-screen view */}
        <div className="pdm-media" onTouchStart={onTS} onTouchEnd={onTE}>
          <div
            className="pdm-stage"
            style={{ backgroundImage: `url('${cur.kind === "video" ? cur.poster : (cur.urlLarge || cur.url)}')`, cursor: cur.kind === "video" ? "default" : "zoom-in" }}
            onClick={(e) => {
              // Don't zoom when clicking the prev/next nav controls or video
              if (e.target.closest(".pdm-nav, video")) return;
              if (cur.kind !== "video") setZoomed(true);
            }}
            role={cur.kind === "video" ? undefined : "button"}
            aria-label={cur.kind === "video" ? undefined : "Tap to view full size"}
            data-testid="pdm-stage-zoom"
          >
            {cur.kind === "video" && (
              <video key={cur.key} src={cur.url} poster={cur.poster} controls autoPlay playsInline />
            )}
            {cur.kind !== "video" && (
              <button
                type="button"
                className="pdm-zoom-hint"
                aria-label="View full size"
                onClick={(e) => { e.stopPropagation(); setZoomed(true); }}
              >⤢</button>
            )}
          </div>
          {total > 1 && (
            <>
              <button
                type="button"
                className="pdm-nav prev"
                disabled={idx === 0}
                onClick={() => setIdx(Math.max(0, idx - 1))}
                aria-label="Previous"
              >‹</button>
              <button
                type="button"
                className="pdm-nav next"
                disabled={idx === total - 1}
                onClick={() => setIdx(Math.min(total - 1, idx + 1))}
                aria-label="Next"
              >›</button>
              <div className="pdm-thumbs">
                {items.map((it, k) => (
                  <button
                    key={it.key}
                    type="button"
                    className={`pdm-thumb${k === idx ? " on" : ""}`}
                    onClick={() => setIdx(k)}
                    aria-label={`Media ${k + 1}`}
                    style={{ backgroundImage: `url('${it.kind === "video" ? it.poster : it.url}')` }}
                  >
                    {it.kind === "video" && <span className="pdm-thumb-play">▶</span>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right: info column */}
        <div className="pdm-info">
          <div className="pdm-eye">— {p.section_label || defaultLabel(p.category)} —</div>
          <h2 className="pdm-title">{p.name || "Product"}</h2>

          <div className="pdm-row">
            <span className={`pdm-pill ${avCls}`}>{av}</span>
            {p.badge && <span className="pdm-pill gold">{p.badge}</span>}
            {tags.map((t) => <span key={t} className="pdm-pill">{t}</span>)}
          </div>

          {/* Structured pricing tiers · the centerpiece of the Drop Builder. */}
          {Array.isArray(p.tiers) && p.tiers.length > 0 ? (
            <TierPicker product={p} mixHint={
              p.category === "breakdown"  ? "Mix any quantity — no minimum, no max." :
              p.category === "flower"     ? "Add as many or as few as you want." :
              p.category === "wholesale"  ? "Pick a quantity — we'll confirm by Telegram." :
              ""
            } />
          ) : (
            p.price && (
              p.on_sale && p.compare_at_price ? (
                <div className="pdm-price" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ padding: "3px 9px", borderRadius: 4, background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407", fontWeight: 700, letterSpacing: ".16em", fontSize: 10 }}>SALE</span>
                  <s style={{ color: "#6e6f5f", fontSize: "0.7em", fontWeight: 400 }}>{p.compare_at_price}</s>
                  <span>{p.price}</span>
                </div>
              ) : (
                <div className="pdm-price">{p.price}</div>
              )
            )
          )}
          {p.size && !p.tiers?.length && <div className="pdm-size">{p.size}</div>}

          {/* Flavor / effect / best-for trait chips */}
          <ProductTraits p={p} />

          {p.description && (
            <div className="pdm-section">
              <div className="pdm-h">Description</div>
              <RichText text={p.description} />
            </div>
          )}
          {p.notes && (
            <div className="pdm-section">
              <div className="pdm-h">Tasting Notes</div>
              <RichText text={p.notes} />
            </div>
          )}

          <div className="pdm-actions">
            {Array.isArray(p.tiers) && p.tiers.length > 0 ? (
              <button
                type="button"
                className="pdm-btn"
                onClick={() => { onClose(); inquire.open({ productName: p.name }); }}
                data-testid="pdm-inquire-btn"
              >Inquire</button>
            ) : (
              <button
                type="button"
                className="pdm-btn solid"
                onClick={() => { onClose(); inquire.open({ productName: p.name }); }}
                data-testid="pdm-inquire-btn"
              >Inquire to Order</button>
            )}
            <button
              type="button"
              className="pdm-btn"
              onClick={() => save(p)}
              data-testid="pdm-save-btn"
            >Save Item</button>
          </div>

          <div className="pdm-legal">
            21+ Members only · Confirm by Telegram · Discreet drop
          </div>
        </div>
      </div>

      {/* Fullscreen image lightbox — tap any photo on the modal to zoom in.
          Backdrop close on outside-click is gated to only fire when the click
          target IS the backdrop element itself (not a child); prevents the
          lingering-DOM bug where rapid tap-close-tap was racing React unmount. */}
      {zoomed && cur.kind !== "video" && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="pdm-zoom-lightbox"
          onClick={(e) => { if (e.target === e.currentTarget) setZoomed(false); }}
          style={{
            position: "fixed", inset: 0,
            // z-index sits ABOVE both .pdm-backdrop (200) AND the public top
            // nav members-pill (1000). 20000 gives us comfortable headroom for
            // any future overlay layers (popups, age gate, intro animation).
            zIndex: 20000,
            background: "rgba(2,4,12,.98)",
            backdropFilter: "blur(10px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "5vh 4vw",
            cursor: "zoom-out",
          }}
        >
          <button
            type="button"
            aria-label="Close zoom"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            style={{
              position: "absolute",
              top: "max(18px, env(safe-area-inset-top, 18px))",
              right: 18,
              zIndex: 20001,
              width: 48, height: 48, borderRadius: 24,
              background: "rgba(20,20,20,.92)", color: "#fff",
              border: "1px solid rgba(255,255,255,.25)",
              cursor: "pointer", fontSize: 20, fontWeight: 700,
              boxShadow: "0 8px 24px rgba(0,0,0,.6)",
            }}
            data-testid="pdm-zoom-close"
          >✕</button>
          <img
            src={cur.urlLarge || cur.url}
            alt={p.name}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "100%", maxHeight: "100%",
              width: "auto", height: "auto",
              objectFit: "contain",
              borderRadius: 8,
              boxShadow: "0 20px 60px rgba(0,0,0,.6)",
            }}
          />
        </div>
      )}
    </div>
  ), document.body);
}

/* ============================== RICH TEXT ==============================
   Renders multiline description/notes with proper formatting:
   • Blank lines → paragraph breaks (spacing between blocks)
   • Lines starting with • / - / * / ✔ / ✅ → bullet list (indented)
   • Single-line breaks within a paragraph are preserved
   • Heading-style lines ending with ":" get a subtle weight bump

/* ============================== TIER PICKER ==============================
   The mix-and-match weight selector inside the product modal. Large tactile
   pills, smooth animations, instant pricing. NOT a boring dropdown.
   ======================================================================== */
function TierPicker({ product, mixHint }) {
  const drop = useDrop();
  const tiers = (product.tiers || []).filter(Boolean);
  const firstAvailable = tiers.find((t) => t.in_stock !== false) || tiers[0];
  const [selectedKey, setSelectedKey] = React.useState(firstAvailable?.key || "");
  const [added, setAdded] = React.useState(false);
  const [pulseKey, setPulseKey] = React.useState("");

  // Listen for the cinematic banner's step-clicked event so we can pulse
  // the matching weight pill (visual breadcrumb for the user).
  React.useEffect(() => {
    const onPulse = (e) => {
      const k = (e?.detail?.key || "").toUpperCase();
      if (!k) return;
      // Find a tier whose key matches (case-insensitive)
      const match = tiers.find((t) => (t.key || "").toUpperCase() === k);
      if (!match) return;
      setSelectedKey(match.key);
      setPulseKey(match.key);
      setTimeout(() => setPulseKey(""), 1800);
    };
    window.addEventListener("2high:tier-pulse", onPulse);
    return () => window.removeEventListener("2high:tier-pulse", onPulse);
  }, [tiers]);

  const selected = tiers.find((t) => t.key === selectedKey) || firstAvailable;

  function pickFirstImg(p) {
    const cover = p.img_set?.md || p.img_set?.sm || p.img;
    if (cover) return cover;
    if (Array.isArray(p.photos) && p.photos[0]) {
      return p.photos[0].set?.md || p.photos[0].set?.sm || p.photos[0].url;
    }
    return "";
  }

  function handleAdd() {
    if (!selected || selected.in_stock === false) return;
    drop.add({
      product_id: product.id,
      product_name: product.name || "",
      tier_key: selected.key,
      tier_label: selected.label || selected.key,
      unit_price: Number(selected.price) || 0,
      qty: 1,
      img: pickFirstImg(product),
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  }

  if (!tiers.length) return null;

  const priceLabel = selected ? money(selected.price) : "";

  return (
    <div className="pdm-tier-section" data-testid="pdm-tiers">
      <div className="pdm-tier-head">
        <h4>Pick Your Weight</h4>
        {mixHint && <span className="pdm-tier-mix">{mixHint}</span>}
      </div>
      <div className="pdm-tier-grid" data-testid="pdm-tier-grid">
        {tiers.map((t) => {
          const isSelected = t.key === selectedKey;
          const outOfStock = t.in_stock === false;
          return (
            <button
              type="button"
              key={t.key || t.label}
              className={`pdm-tier-pill ${isSelected ? "on" : ""} ${outOfStock ? "out" : ""} ${pulseKey === t.key ? "pulse" : ""}`}
              onClick={() => !outOfStock && setSelectedKey(t.key)}
              disabled={outOfStock}
              data-testid={`pdm-tier-${t.key || t.label}`}
              aria-pressed={isSelected}
            >
              <span className="pdm-tier-key">{t.key || t.label}</span>
              <span className="pdm-tier-price">
                {Number(t.price) > 0 ? money(t.price) : (t.note || "—")}
              </span>
              {outOfStock && <span className="pdm-tier-out">Out</span>}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 18 }}>
        <button
          type="button"
          className="pdm-add-drop"
          onClick={handleAdd}
          disabled={!selected || selected.in_stock === false || Number(selected?.price) <= 0}
          data-testid="pdm-add-to-drop"
        >
          {added ? (
            <span className="added">✓ Added to your drop</span>
          ) : (
            <>Add {selected?.label || selected?.key} · {priceLabel} to Drop</>
          )}
        </button>
      </div>
    </div>
  );
}

/* ============================== PRODUCT TRAITS ===========================
   Flavor / effect / best-for chips. Hidden gracefully when empty.
   ======================================================================== */
function ProductTraits({ p }) {
  const flavors = (p.flavor_tags || []).filter(Boolean);
  const effects = (p.effect_tags || []).filter(Boolean);
  const bestFor = (p.best_for || []).filter(Boolean);
  if (!flavors.length && !effects.length && !bestFor.length) return null;

  return (
    <div className="pdm-traits" data-testid="pdm-traits">
      {flavors.length > 0 && (
        <div className="pdm-trait-row">
          <div className="pdm-trait-h">Flavor Profile</div>
          <div className="pdm-trait-chips">
            {flavors.map((f) => <span key={f} className="pdm-trait-chip flavor">{f}</span>)}
          </div>
        </div>
      )}
      {effects.length > 0 && (
        <div className="pdm-trait-row">
          <div className="pdm-trait-h">Effects</div>
          <div className="pdm-trait-chips">
            {effects.map((e) => <span key={e} className="pdm-trait-chip effect">{e}</span>)}
          </div>
        </div>
      )}
      {bestFor.length > 0 && (
        <div className="pdm-trait-row">
          <div className="pdm-trait-h">Best For</div>
          <div className="pdm-trait-chips">
            {bestFor.map((b) => <span key={b} className="pdm-trait-chip">{b}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function RichText({ text }) {
  if (!text || typeof text !== "string") return null;

  // Split into "blocks" separated by blank lines
  const blocks = text.replace(/\r\n/g, "\n").split(/\n\s*\n/);

  return (
    <div className="rich-text" style={{ fontSize: "inherit", lineHeight: 1.7 }}>
      {blocks.map((blk, bi) => {
        const lines = blk.split("\n").map((l) => l.trimEnd()).filter(Boolean);
        if (lines.length === 0) return null;

        // Detect bullet block: most lines start with a bullet marker
        const bulletPattern = /^\s*([•\-*▪‣◦]|✔️?|✅|☑️?|►|→)\s+/;
        const bulletLines = lines.filter((l) => bulletPattern.test(l));
        const isBulletBlock = bulletLines.length >= Math.max(2, Math.ceil(lines.length * 0.6));

        if (isBulletBlock) {
          return (
            <ul key={bi} style={{ margin: "0 0 1em 0", padding: "0 0 0 1.1em", listStyle: "none" }}>
              {lines.map((line, li) => {
                const cleaned = line.replace(bulletPattern, "");
                const m = bulletPattern.exec(line);
                const marker = m ? m[1] : "•";
                return (
                  <li key={li} style={{ margin: "0.35em 0", position: "relative", paddingLeft: "0.2em" }}>
                    <span style={{ position: "absolute", left: "-1.1em", color: "var(--accent, #c9a86b)", fontWeight: 600 }}>{marker}</span>
                    <RichInline text={cleaned} />
                  </li>
                );
              })}
            </ul>
          );
        }

        // Paragraph block — preserve single-line breaks inside
        return (
          <p key={bi} style={{ margin: "0 0 1em 0", whiteSpace: "pre-line" }}>
            {lines.map((line, li) => {
              const isHeading = /:$/.test(line) && line.length < 70;
              return (
                <React.Fragment key={li}>
                  <span style={isHeading ? { fontWeight: 600, color: "var(--accent, #c9a86b)" } : undefined}>
                    <RichInline text={line} />
                  </span>
                  {li < lines.length - 1 && <br />}
                </React.Fragment>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}

/* Tiny inline helper — highlights "Key:" labels at the start of lines so
   structured product specs scan beautifully. */
function RichInline({ text }) {
  const m = /^([A-Za-z][A-Za-z &/]{2,28}):\s+(.+)$/.exec(text);
  if (m) {
    return (
      <>
        <span style={{ color: "var(--accent, #c9a86b)", fontWeight: 600 }}>{m[1]}:</span>{" "}
        <span>{m[2]}</span>
      </>
    );
  }
  return <>{text}</>;
}
